import { useCallback, useRef } from 'react';

import { Box, createStyles, Group, Stack, Tooltip } from '@mantine/core';
import { KeyboardShortcut } from '@repo/common/components/KeyboardShortcut/KeyboardShortcut';
import { IconButton, Text } from '@repo/foundations';
import { MOD_KEY } from '@repo/secoda-editor/utils/keyboard';
import { observer } from 'mobx-react-lite';
import type { SecodaEditor } from '../../../components/Editor/SecodaEditor';
import type { QuestionStore } from '../store';
import { QuestionReplyInput } from './QuestionReplyInput';

const useStyles = createStyles((theme) => ({
	container: {
		gap: 0,
		borderRadius: theme.radius.md,
		boxShadow: theme.shadows.sm,
		border: 'none',
		backgroundColor: theme.other.getColor('surface/primary/default'),
	},
	controls: {
		padding: `0 ${theme.spacing.xs} ${theme.spacing.xs} ${theme.spacing.md}`,
		justifyContent: 'space-between',
		alignItems: 'center',
		flexWrap: 'nowrap',
	},
	input: {
		// CSS for RichEditor
		flex: '1 !important', // The editor forces `flex-grow: 0` when in readOnly mode
		padding: theme.spacing.md,
		maxHeight: '300px',
		overflowY: 'auto',

		'&:hover,&:focus': {
			'&::-webkit-scrollbar-thumb': {
				backgroundColor: theme.other.getColor('fill/primary/selected'),
			},
		},
		// Scrollbar styling
		'&::-webkit-scrollbar': {
			backgroundColor: 'transparent',
			width: theme.spacing.xs,
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'transparent',

			borderRadius: theme.radius.sm,
		},
		'&::-webkit-scrollbar-thumb:hover': {
			backgroundColor: theme.other.getColor('fill/primary/selected'),
		},
		// Outline editor
		'& .ProseMirror > *:last-child': {
			marginBottom: 0,
		},
	},
}));

export const QuestionNewReply = observer(
	({ store }: { store: QuestionStore }) => {
		const { classes } = useStyles();
		const containerRef = useRef<HTMLDivElement>(null);
		const editorRef = useRef<SecodaEditor>(null);

		const handleChange = useCallback(
			(value: string | undefined) => {
				store.setReplyInputValue(value ?? '');
			},
			[store]
		);

		const handleClickContainer = useCallback(
			(event: React.MouseEvent<HTMLDivElement>) => {
				if (
					event.target === containerRef.current ||
					(event.target as HTMLElement).classList.contains('focus-input')
				) {
					editorRef.current?.focus();
				}
			},
			[editorRef]
		);

		return (
			<Stack
				ref={containerRef}
				className={`${classes.container} focus-input`}
				onClick={handleClickContainer}
			>
				<QuestionReplyInput
					key={store.replyEditorKey}
					onSubmit={store.createReply}
					onChange={handleChange}
					dataTestId="new-reply-editor"
					emptyLinePlaceholder={'Write something or press "/" for actions...'}
					emptyDocPlaceholder="Add reply..."
					initialValue={store.replyInputValue}
					readOnly={false}
					className={classes.input}
					editorRef={editorRef}
				/>
				<Group className={`${classes.controls} focus-input`}>
					<Box>
						{store.question.native_type === 'slack_thread' && (
							<Text color="text/secondary/default" size="xs">
								Reply will be sent to Slack thread
							</Text>
						)}
					</Box>
					<Tooltip
						withArrow
						arrowSize={8}
						disabled={!store.replyInputValue.length}
						label={
							<KeyboardShortcut keys={[MOD_KEY, 'Enter']} variant="dark">
								Send message
							</KeyboardShortcut>
						}
						position="top"
					>
						<IconButton
							variant="primary"
							iconName="arrowUp"
							data-testid="submit-button"
							disabled={!store.replyInputValue.length || store.replySubmitting}
							loading={store.replySubmitting}
							size="md"
							display="block"
							onClick={store.createReply}
						/>
					</Tooltip>
				</Group>
			</Stack>
		);
	}
);
